import { useEffect, useRef } from 'react';
import maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import type { Place } from '@wingman/shared';
import { DEFAULT_MAP_CENTER, DEFAULT_MAP_ZOOM, CATEGORIES } from '@wingman/shared';

export type MapVisualMode = 'raw' | 'blueprint';

interface MapViewProps {
  places: Place[];
  selectedPlace: Place | null;
  onPlaceClick: (place: Place) => void;
  onMapMove: (bbox: { minLng: number; minLat: number; maxLng: number; maxLat: number }) => void;
  routeGeometry?: GeoJSON.LineString | null;
  center?: { lat: number; lng: number };
  zoom?: number;
  visualMode?: MapVisualMode;
}

const CATEGORY_COLORS: Record<number, string> = CATEGORIES.reduce((acc, c) => {
  acc[c.id] = c.color;
  return acc;
}, {} as Record<number, string>);

function getMapStyle(tileserverUrl: string, tileType: string, visualMode: MapVisualMode) {
  if (tileType === 'vector' && tileserverUrl) {
    const stylePath = visualMode === 'raw' ? '/styles/dark.json' : '/styles/default.json';
    return `${tileserverUrl}${stylePath}`;
  }
  const hasTileserver = tileserverUrl && !tileserverUrl.startsWith('http://localhost');
  const tiles = hasTileserver
    ? [`${tileserverUrl}/data/{z}/{x}/{y}.png`]
    : ['https://tile.openstreetmap.org/{z}/{x}/{y}.png'];
  const raw = visualMode === 'raw';
  return {
    version: 8 as const,
    sources: {
      'osm-tiles': {
        type: 'raster' as const,
        tiles,
        tileSize: 256,
        attribution: '&copy; OpenStreetMap contributors',
      },
    },
    layers: [
      {
        id: 'system-background',
        type: 'background' as const,
        paint: { 'background-color': raw ? '#0d0d0d' : '#071b25' },
      },
      {
        id: 'osm-layer',
        type: 'raster' as const,
        source: 'osm-tiles',
        paint: raw
          ? {
              'raster-opacity': 0.42,
              'raster-saturation': -1,
              'raster-contrast': 0.72,
              'raster-brightness-min': 0,
              'raster-brightness-max': 0.36,
            }
          : {
              'raster-opacity': 0.58,
              'raster-saturation': -0.8,
              'raster-contrast': 0.42,
              'raster-hue-rotate': 155,
              'raster-brightness-min': 0.04,
              'raster-brightness-max': 0.52,
            },
      },
    ],
  } as maplibregl.StyleSpecification;
}

export default function MapView({
  places,
  selectedPlace,
  onPlaceClick,
  onMapMove,
  routeGeometry,
  center,
  zoom,
  visualMode = 'raw',
}: MapViewProps) {
  const mapContainer = useRef<HTMLDivElement>(null);
  const mapRef = useRef<maplibregl.Map | null>(null);
  const clusterRef = useRef<maplibregl.GeoJSONSource | null>(null);
  const popupRef = useRef<maplibregl.Popup | null>(null);

  useEffect(() => {
    if (!mapContainer.current || mapRef.current) return;

    const tileserverUrl = import.meta.env.VITE_TILESERVER_URL || '';
    const tileType = import.meta.env.VITE_TILE_TYPE || 'raster';
    const style = getMapStyle(tileserverUrl, tileType, visualMode);

    const map = new maplibregl.Map({
      container: mapContainer.current,
      style,
      center: [center?.lng || DEFAULT_MAP_CENTER.lng, center?.lat || DEFAULT_MAP_CENTER.lat],
      zoom: zoom || DEFAULT_MAP_ZOOM,
    });

    map.addControl(new maplibregl.NavigationControl(), 'top-right');
    map.addControl(
      new maplibregl.GeolocateControl({
        positionOptions: { enableHighAccuracy: true },
        trackUserLocation: true,
      }),
    );

    map.on('moveend', () => {
      const bounds = map.getBounds();
      onMapMove({
        minLng: bounds.getWest(),
        minLat: bounds.getSouth(),
        maxLng: bounds.getEast(),
        maxLat: bounds.getNorth(),
      });
    });

    map.on('load', () => {
      map.addSource('places', {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
        cluster: true,
        clusterRadius: 50,
        clusterMaxZoom: 14,
      });

      map.addLayer({
        id: 'clusters',
        type: 'circle',
        source: 'places',
        filter: ['has', 'point_count'],
        paint: {
          'circle-color': '#00f5ff',
          'circle-radius': ['step', ['get', 'point_count'], 16, 10, 22, 50, 30],
          'circle-opacity': 0.18,
          'circle-stroke-width': 1,
          'circle-stroke-color': '#00f5ff',
          'circle-blur': 0.12,
        },
      });

      map.addLayer({
        id: 'cluster-count',
        type: 'symbol',
        source: 'places',
        filter: ['has', 'point_count'],
        layout: {
          'text-field': '{point_count_abbreviated}',
          'text-size': 13,
          'text-font': ['Noto Sans Mono Regular', 'Noto Sans Regular'],
        },
        paint: {
          'text-color': '#00f5ff',
          'text-halo-color': 'rgba(0,245,255,0.32)',
          'text-halo-width': 1,
        },
      });

      map.addLayer({
        id: 'unclustered-point-halo',
        type: 'circle',
        source: 'places',
        filter: ['!', ['has', 'point_count']],
        paint: {
          'circle-color': 'rgba(0, 245, 255, 0.22)',
          'circle-radius': ['case', ['==', ['get', 'id'], selectedPlace?.id || ''], 20, 0],
          'circle-opacity': 0.65,
        },
      });

      map.addLayer({
        id: 'unclustered-point',
        type: 'circle',
        source: 'places',
        filter: ['!', ['has', 'point_count']],
        paint: {
          'circle-color': [
            'match',
            ['get', 'categoryId'],
            ...Object.entries(CATEGORY_COLORS).flatMap(([id, color]) => [Number(id), color]) as unknown[],
            '#00f5ff',
          ] as unknown as maplibregl.ExpressionSpecification,
          'circle-radius': ['case', ['==', ['get', 'id'], selectedPlace?.id || ''], 13, 8],
          'circle-opacity': 0.88,
          'circle-stroke-width': ['case', ['==', ['get', 'id'], selectedPlace?.id || ''], 3, 1],
          'circle-stroke-color': ['case', ['==', ['get', 'id'], selectedPlace?.id || ''], '#ffffff', '#00f5ff'],
          'circle-blur': 0.03,
        },
      });

      map.on('click', 'clusters', (e) => {
        const features = map.queryRenderedFeatures(e.point, { layers: ['clusters'] });
        const clusterId = features[0]?.properties?.cluster_id;
        if (clusterId === undefined) return;
        const source = map.getSource('places') as maplibregl.GeoJSONSource;
        source.getClusterExpansionZoom(clusterId).then((zoom: number) => {
          const coords = (features[0].geometry as GeoJSON.Point).coordinates;
          map.flyTo({ center: coords as [number, number], zoom, essential: true });
        });
      });

      map.on('click', 'unclustered-point', (e) => {
        const props = e.features?.[0]?.properties;
        if (!props) return;
        const place = places.find((p) => p.id === props.id);
        if (place) onPlaceClick(place);
      });

      map.on('mouseenter', 'unclustered-point', (e) => {
        map.getCanvas().style.cursor = 'pointer';
        const props = e.features?.[0]?.properties;
        if (!props) return;
        const coords = (e.features?.[0]?.geometry as GeoJSON.Point)?.coordinates as [number, number] ?? [0, 0];
        const place = places.find((p) => p.id === props.id);
        const escapeHtml = (str: string) => str.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]!));
        const name = escapeHtml(place?.name || props.name || 'Place');
        const categoryName = escapeHtml(place?.categoryName || '');
        const rating = place?.rating;

        const html = `
          <div style="font-weight:600;font-size:14px;color:#1f2937;">${name}</div>
          ${categoryName ? `<div style="font-size:11px;color:#6b7280;margin-top:2px;">${categoryName}</div>` : ''}
          ${rating ? `<div style="font-size:12px;color:#f59e0b;margin-top:2px;">★ ${rating.toFixed(1)}</div>` : ''}
          <div style="font-size:10px;color:#9ca3af;margin-top:4px;">Click for details</div>
        `;
        popupRef.current?.remove();
        popupRef.current = new maplibregl.Popup({ closeButton: false, closeOnClick: false, offset: 12 })
          .setLngLat(coords)
          .setHTML(html)
          .addTo(map);
      });

      map.on('mouseleave', 'unclustered-point', () => {
        map.getCanvas().style.cursor = '';
        popupRef.current?.remove();
        popupRef.current = null;
      });

      map.on('mouseenter', 'clusters', () => {
        map.getCanvas().style.cursor = 'pointer';
      });
      map.on('mouseleave', 'clusters', () => {
        map.getCanvas().style.cursor = '';
      });
    });

    mapRef.current = map;

    const bounds = map.getBounds();
    onMapMove({
      minLng: bounds.getWest(),
      minLat: bounds.getSouth(),
      maxLng: bounds.getEast(),
      maxLat: bounds.getNorth(),
    });

    return () => {
      popupRef.current?.remove();
      map.remove();
      mapRef.current = null;
    };
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !map.getSource('places')) return;

    const features: GeoJSON.Feature<GeoJSON.Point>[] = places.map((p) => ({
      type: 'Feature',
      geometry: { type: 'Point', coordinates: [p.lng, p.lat] },
      properties: {
        id: p.id,
        name: p.name,
        categoryId: p.categoryId,
        rating: p.rating,
      },
    }));

    (map.getSource('places') as maplibregl.GeoJSONSource).setData({
      type: 'FeatureCollection',
      features,
    });
  }, [places]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !map.getLayer('unclustered-point')) return;
    map.setPaintProperty('unclustered-point', 'circle-radius', [
      'case',
      ['==', ['get', 'id'], selectedPlace?.id || ''],
      12,
      8,
    ]);
  }, [selectedPlace]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    if (routeGeometry) {
      if (map.getSource('route')) {
        (map.getSource('route') as maplibregl.GeoJSONSource).setData({
          type: 'Feature',
          properties: {},
          geometry: routeGeometry,
        });
      } else {
        map.addSource('route', {
          type: 'geojson',
          data: {
            type: 'Feature',
            properties: {},
            geometry: routeGeometry,
          },
        });
        map.addLayer({
          id: 'route-casing',
          type: 'line',
          source: 'route',
          layout: {
            'line-join': 'round',
            'line-cap': 'round',
          },
          paint: {
            'line-color': 'rgba(255,255,255,0.92)',
            'line-width': 11,
            'line-opacity': 0.88,
          },
        });
        map.addLayer({
          id: 'route-layer',
          type: 'line',
          source: 'route',
          layout: {
            'line-join': 'round',
            'line-cap': 'round',
          },
          paint: {
            'line-color': '#0f82f2',
            'line-width': 6,
            'line-opacity': 0.94,
            'line-gradient': ['interpolate', ['linear'], ['line-progress'], 0, '#8b5cf6', 0.35, '#228be6', 1, '#74c0fc'],
            'line-dasharray': [1.4, 1.2],
          },
        });
      }
    } else {
      if (map.getLayer('route-layer')) map.removeLayer('route-layer');
      if (map.getLayer('route-casing')) map.removeLayer('route-casing');
      if (map.getSource('route')) map.removeSource('route');
    }
  }, [routeGeometry]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !center) return;
    map.flyTo({
      center: [center.lng, center.lat],
      zoom: zoom || map.getZoom(),
      essential: true,
    });
  }, [center, zoom]);

  return (
    <div ref={mapContainer} id="map-canvas" className={`map-container map-mode-${visualMode}`} aria-label="Map view" role="application" />
  );
}
